import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Text } from '@inter-ikea-kompis/component-text';
import { Tooltip } from '@inter-ikea-kompis/component-tooltip';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';
import { checkmark, copy, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { Tooltip as SkapaTooltip } from '@ingka/tooltip-webc/passive.js';

import CopyDesignCodeStateEnum from '../../enums/CopyDesignCodeStateEnum.js';
import type ICopyDesignCodeLocalizedInformation from '../../types/ICopyDesignCodeLocalizedInformation.js';
import type ICopyDesignCodeTranslations from '../../types/ICopyDesignCodeTranslations.js';
import CopyDesignCodeStyle from './CopyDesignCodeStyle.js';

/**
 * CopyCode class.
 *
 * @event copy Click on copy button
 * @event reset Sent after 3 seconds after state success.
 */

export default class CopyDesignCode extends KompisElement {
	// Theme
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: ICopyDesignCodeLocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: ICopyDesignCodeTranslations | null = null;

	// VPC code
	@property({ type: String, required: true })
	public vpcCode!: string;

	// State information
	@property({ type: String })
	public state: CopyDesignCodeStateEnum = CopyDesignCodeStateEnum.default;

	private timeout: ReturnType<typeof setTimeout> | null = null;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		const { designCodeTitle } = this.getTranslations();
		const tertiaryVariant: ButtonVariant = 'tertiary';

		return html`
			<style>
				${CopyDesignCodeStyle.getStyle({
					theme: this.theme
				})}
			</style>
			<div class="container">
				<kompis-text
					class="copyDesignCode"
					.theme="${this.theme}"
					.type="${ThemeFontStyleTypeEnum.bodyMedium}"
				>
					${designCodeTitle}
				</kompis-text>
				<kompis-text class="designCode" .bold="${true}" .theme="${this.theme}">
					${this.vpcCode}
				</kompis-text>
				<skapa-tooltip position="trailing">
					<skapa-icon-button
						slot="custom-trigger"
						variant="${tertiaryVariant}"
						size="small"
						@click="${this.onCopyClick.bind(this)}"
					>
						${this.getTooltipText()}
						<skapa-icon
							slot="icon"
							icon="${this.state === CopyDesignCodeStateEnum.success ? 'checkmark' : 'copy'}"
						></skapa-icon>
					</skapa-icon-button>
					${this.getTooltipText()}
				</skapa-tooltip>
			</div>
		`;
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		if (this.timeout) {
			clearTimeout(this.timeout);
		}
	}

	public override async connectedCallback(): Promise<void> {
		registerIcon(copy);
		registerIcon(checkmark);

		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('skapa-tooltip', SkapaTooltip);
		this.customElements.define('kompis-text', Text);
		this.customElements.define('kompis-tooltip', Tooltip);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('state')) {
			const tooltip = this.shadowRoot?.querySelector(
				this.customElements.getInternalName('skapa-tooltip')
			);

			if (this.state === CopyDesignCodeStateEnum.success) {
				tooltip?.setAttribute('toggle', 'true');

				if (this.timeout) {
					clearTimeout(this.timeout);
				}

				this.timeout = setTimeout(() => {
					this.dispatchEvent(new CustomEvent('reset', { bubbles: true }));
				}, 3000);
			} else if (this.state === CopyDesignCodeStateEnum.default) {
				tooltip?.removeAttribute('toggle');
			}
		}

		return true;
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation?.translations;
	}

	private getTranslations(): ICopyDesignCodeTranslations {
		return this.translations
			? this.translations
			: <ICopyDesignCodeTranslations>this.localizedInformation?.translations;
	}
	/**
	 * Message to user.
	 *
	 * @returns Template.
	 */
	private getTooltipText(): RenderResult {
		if (this.state === CopyDesignCodeStateEnum.default) {
			return this.getTranslations().copyCodeTooltip;
		}

		return this.getTranslations().copyCodeSuccessMessage;
	}

	/**
	 * Click event for button for copy.
	 */
	private async onCopyClick(): Promise<void> {
		let thrownError: Error | null = null;

		try {
			await navigator.clipboard.writeText(this.vpcCode);
		} catch (error) {
			thrownError = error;
		}

		this.dispatchEvent(
			new CustomEvent('copy', {
				bubbles: true,
				detail: {
					state: !thrownError ? CopyDesignCodeStateEnum.success : CopyDesignCodeStateEnum.default,
					error: thrownError?.message
				}
			})
		);

		if (thrownError) {
			throw thrownError;
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'skapa-icon': SkapaIcon;
		'skapa-tooltip': SkapaTooltip;
		'kompis-copy-design-code': CopyDesignCode;
	}
}
